<template>
	<div class="account__section__container deposit">
		<payments-loader v-if="containerLoading" />
		<div v-else class="account__section global-card__type--block">
			<h3
				:class="{
					account__section__title: 1,
					deposit__title: 1,
				}"
			>
				{{ this.$t("account.deposit") }}
			</h3>
			<p
				:class="{
					account__section__desc: 1,
					deposit__desc: 1,
					'deposit__desc--error': context == 'error',
				}"
			>
				{{ this.$t("account.send-money-into-your-betika-account") }}
			</p>
			<div
				class="button__group account__section__button-group"
				style="margin-bottom: 10px; margin-top: 15px"
			>
				<button
					:class="{
						button: 1,
						rounded: 1,
						account__section__button: 1,
						button__secondary: value == selectedButton,
					}"
					:disabled="transacting"
					v-for="value in depositButtons"
					:key="value"
					@click="
						() => {
							amount += value;
							selectedButton = value;
						}
					"
				>
					+{{ value }}
				</button>
			</div>
			<error v-if="error">{{ error }}</error>

			<b-input
				:class="{ deposit: 1, input: 1, account__section__input: 1 }"
				name="deposit-amount"
				type="number"
				min="50"
				step="50"
				autocomplete="off"
				:placeholder="$t('account.enter-amount-to-deposit')"
				:error="error"
				:disabled="loading || transacting"
				v-model="amount"
				style="margin-bottom: 10px"
				><small slot="desc"
					>{{ $t("account.minimum") }}
					{{ env.minimumDepositAmount | currency }}.
					{{ $t("account.tax-message") }}</small
				></b-input
			>
			<template v-if="env.availableFeatures.paymentProviders">
				<h3 class="account__section__title">
					{{ $t("account.select-payment-provider") }}
				</h3>
				<b-checkbox
					:value="selectedProvider.name == provider.name"
					v-for="provider in depositProviders"
					:key="provider.name"
					:label="$options.filters.toTitleCase(provider.name)"
					:name="'provider'"
					@click.native="setProvider(provider)"
					class="session__form__checkbox session__form__remember"
					:disabled="loading"
				/>
			</template>

			<div
				v-if="
					!isMiniApp &&
					cashiaSupportsUserNetwork &&
					isFeatureOn('CASHIA_WALLET')
				"
			>
				<b-button-ii
					:disabled="transacting"
					:loading="loading"
					:title="$t('session.wallet.btn.m-pesa')"
					type="primary"
					rounded-small
					:fit-text="true"
					full-width
					class="button button__secondary account__section__submit cashia"
					@click="deposit"
				>
					<span class="img-cover">
						<img src="/static/images/mpesa-logo.png"/>
					</span>
				</b-button-ii>
				<WalletCashia
					:amount="amount" @validation-error="handleValidationError"/>
			</div>
			<b-button-ii
				v-else-if="isMiniApp && miniappLoaded && isMiniappSupportedNetwork"
				:disabled="transacting"
				:loading="loading"
				:title="$t(env.miniapp.cta.title)"
				type="primary"
				rounded-small
				:fit-text="true"
				full-width
				class="button button__secondary account__section__submit cashia"
				@click="deposit"
			>
				<span class="img-cover">
					<img :src="env.miniapp.cta.logoUrl" />
				</span>
			</b-button-ii>
			<b-button
				v-else-if="(!cashiaSupportsUserNetwork || !isFeatureOn('CASHIA_WALLET'))"
				class="button button__secondary account__section__submit"
				:disabled="transacting"
				:loading="loading"
				:title="$t('account.deposit')"
				@click="deposit"
			>
			</b-button>
			<!-- Show loading state while feature flags are being loaded -->
			<div v-else class="feature-loading">
				<b-button
					class="button button__secondary account__section__submit"
					:disabled="true"
					:loading="true"
					:title="$t('account.loading')"
				>
				</b-button>
			</div>

			<bottom-sheet
				:ref="'networkSelectBottomSheet'"
				v-if="
					canShowBottomSheet &&
					env.availableFeatures.network &&
					hasKyc
				"
				@close="closeBottomSheet"
				@has-mounted="showBottomSheet"
			>
				<div style="padding: 20px">
					<network-saver
						:available-networks="availableNetworks"
						:deposit-amount="amount"
						@close="close"
					/>
				</div>
			</bottom-sheet>
		</div>
	</div>
</template>

<script>
import PaymentsLoader from "./PaymentsLoader.vue";
import NetworkSaver from "./NetworkSaver.vue";
import WalletCashia from "./WalletCashia.vue";

export default {
	components: {
		PaymentsLoader,
		NetworkSaver,
		WalletCashia,
	},
	props: {
		injectedAmount: {
			type: Number,
			required: false,
			default: null,
		},
		title: {
			type: String,
			required: false,
			default: "Deposit",
		},
		desc: {
			type: String,
			required: false,
			default: "Send money into your Betika account",
		},
		context: {
			type: String,
			required: false,
			default: "info",
		},
		user: {
			type: Object,
			required: true,
		},
		transacting: {
			type: Boolean,
			default: false,
		},
		containerLoading: {
			type: Boolean,
			default: false,
		},
	},
	created() {
		let vm = this;
		vm.$root.$on("perform-deposit", vm.performDeposit);
		vm.$root.$on("showNetworkSelector", vm.showBottomSheet);
	},
	beforeDestroy() {
		let vm = this;
		vm.$root.$off("perform-deposit", vm.performDeposit);
		vm.$root.$off("showNetworkSelector", vm.showBottomSheet);
		vm.$miniappDeposit.removeAppxScript();
	},
	computed: {
		hasKyc() {
			if (this.env.kyc.required === true) {
				return this.$store.getters.user.has_kyc;
			} else {
				return true;
			}
		},
		availableNetworks() {
			return this.env.networkProviders;
		},
		depositButtons() {
			return this.env.depositAmounts;
		},
		cashiaSupportsUserNetwork(){
			let vm = this;

			const token = vm.$auth.getToken();
			if (!token) {
				vm.$auth.logout();
				vm.$notifications.add({
					message: this.$t("session.expired"),
					type: "error",
					title: this.$t("session.session-expired"),
					duration: 5000,
				});
				vm.$router.replace("/login");
			}

			const userNetwork = this.$store.getters.user.network || "";

			if(!userNetwork) {
				return false;
			}

			const supportedExperimentNetworks = vm.env.cashia.supportedNetworks;
			return supportedExperimentNetworks.includes(userNetwork);
		},
		isGrowthBookInitialized() {
			return this.$store.getters['growthbook/isGrowthBookInitialized'];
		},
	},
	data() {
		return {
			selectedButton: null,
			amount: null,
			loading: true,
			error: false,
			depositProviders: [{ name: "Loading" }, { name: "Loading" }],
			canShowBottomSheet: false,
			selectedProvider: {
				name: "interswitch",
			},
			miniappLoaded: false,
		};
	},
	watch: {
		amount(n, o) {
			n > 1000000 ? (this.amount = Number(o)) : (this.amount = Number(n));
			n < 0 ? (this.amount = 50) : (this.amount = Number(n));
		},
	},
	async beforeMount() {
		let vm = this;

		if (vm.isMiniApp && vm.isMiniappSupportedNetwork) {
			const miniapp = await vm.$miniappDeposit.loadAppxScript(
				vm.env.miniapp.scriptSrc
			);
			vm.miniappLoaded = miniapp.scriptLoaded;
			if (vm.miniappLoaded) {
				// console.warn("Miniapp loaded");
				// console.warn(miniapp.element);
			}
		}

		if (this.env.availableFeatures.paymentProviders) {
			this.getDepositProviders();
		} else {
			this.loading = false;
		}
		if (this.injectedAmount) this.amount = this.injectedAmount;
	},

	methods: {
		setProvider(provider) {
			this.selectedProvider = provider;
		},
		getDepositProviders() {
			let vm = this;
			vm.$axios
				.get(`${BASE_V2_URL}deposit-providers`)
				.then((res) => {
					vm.depositProviders = res.data;
					vm.loading = false;
				})
				.catch((err) => {
					vm.error = true;
					vm.loading = false;
				});
		},
		deposit() {
			let vm = this;
			vm.error = false;
			if (vm.loading || vm.transacting) return;
			if (!vm.selectedProvider.name) return;
			if (vm.amount > 0) {
				vm.loading = true;

				if (vm.amount < vm.env.minimumDepositAmount) {
					vm.loading = false;
					return vm.$notifications.add({
						message:
							this.$t("account.minimum-deposit-amount-is") +
							" " +
							vm.$options.filters.currency(vm.env.minimumDepositAmount),
						title: this.$t("account.deposit-amount"),
						type: "warning",
						duration: 5000,
					});
				}

				if (vm.amount > vm.env.maximumDepositAmount) {
					vm.loading = false;
					return vm.$notifications.add({
						message:
							this.$t("account.maximum-deposit-amount-is") +
							" " +
							vm.$options.filters.currency(vm.env.maximumDepositAmount),
						title: this.$t("account.deposit-amount"),
						type: "warning",
						duration: 5000,
					});
				}

				const token = vm.$auth.getToken();
				if (vm.env.availableFeatures.network) {
					// Call balance endpoint to check network value has been set before performing the deposit
					vm.$axios
						.post(`${BASE_URL}balance`, { token })
						.then((response) => {
							vm.$store.commit("setBalance", response.data.data.balance);
							vm.$store.commit("setKyc", response.data.data.has_kyc);
							vm.$store.commit("SET_KYC_DATA", response.data.data.kyc);

							vm.network = response.data.data.network;

							// Display network selector if the country supports network selection and network value is empty
							if (
								vm.env.availableFeatures.network &&
								(vm.network == null || vm.network == "")
							) {
								// Display network selector if user has not selected a network
								this.$nextTick(() => {
									vm.showBottomSheet();
								});
								this.$nextTick(() => {
									// Emit event to switch from deposit to Network selector in the bottom sheet in Shared/TopSessionButton
									this.$root.$emit(
										"close-deposit-open-network-selector",
										this.amount
									);
								});
								return;
							}

							// Perform Deposit
							if (
								vm.miniappLoaded &&
								vm.isMiniApp &&
								vm.isMiniappSupportedNetwork
							) {
								// console.warn("Initiate Miniapp Deposit");
								let mobile = vm.user.mobile;
								let depositAmount = vm.amount;
								let paybillNo = vm.env.miniapp.businessId;
								let currency = vm.env.currency;
								vm.performMiniappDeposit(
									mobile,
									depositAmount,
									paybillNo,
									currency
								);
								return;
							}
							vm.performDeposit();
						})
						.catch((error) => {
							vm.error = true;
							vm.loading = false;
							if (error.response.status == 401) {
								vm.$auth.logout();
								vm.$router.replace("/login");
								vm.$notifications.add({
									message: this.$t("session.expired"),
									type: "error",
									title: this.$t("session.session-expired"),
									duration: 5000,
								});
								return;
							}
						});
				} else {
					// Perform deposit
					if (
						vm.miniappLoaded &&
						vm.isMiniApp &&
						vm.isMiniappSupportedNetwork
					) {
						// console.warn("Initiate Miniapp Deposit");
						let mobile = vm.user.mobile;
						let depositAmount = vm.amount;
						let paybillNo = vm.env.miniapp.businessId;
						let currency = vm.env.currency;
						vm.performMiniappDeposit(
							mobile,
							depositAmount,
							paybillNo,
							currency
						);
						return;
					}
					vm.performDeposit();
				}
			} else {
				vm.error = this.$t("account.please-enter-an-amount");
			}
		},
		showBottomSheet() {
			this.canShowBottomSheet = true;
			this.loading = false;
			this.$emit("is-transacting", false);
			this.$nextTick(() => {
				this.$refs.networkSelectBottomSheet.setState("open");
			});
		},
		closeBottomSheet() {
			this.$refs.networkSelectBottomSheet.setState("close");
			this.loading = false;
			this.$emit("is-transacting", false);
			this.$nextTick(() => {
				this.canShowBottomSheet = false;
			});
		},
		performDeposit(depositAmount = null) {
			let vm = this;
			if (depositAmount != null) {
				// set amount from event payload
				vm.amount = depositAmount;
			}

			let appSrc = vm.$utils.getAppSource();

			const token = vm.$auth.getToken();
			let payload = {
				src: appSrc,
				amount: vm.amount,
				token,
				//TODO add language
				//"language": "en"
			};

			if (
				vm.env.availableFeatures.paymentProviders &&
				isNetworkSelected == false
			) {
				payload.provider = vm.selectedProvider.name;
			}

			if(
				vm.env.availableFeatures.network &&
				(vm.network !== null && vm.network !== "")
			){
				payload.provider = vm.network;
			}

			this.analyticsTrack("deposit", "attempt", {
				mobile: vm.user.mobile,
				user_id: vm.user.id,
				wallet_balance: vm.user.balance,
				depositAmount: vm.$options.filters.currency(vm.amount),
				depositMethod: "ONLINE",
			});

			vm.$emit("is-transacting", true);
			let url =
				vm.env.depositWithdrawVersion == "v1"
					? `${BASE_URL}deposit`
					: `${BASE_V2_URL}deposit`;

			let successMessage = this.$t(
				"account.success-please-check-your-phone-to-complete-the-deposit"
			);
			let errorMessage = "";

			// Perform the Deposit
			vm.$axios
				.post(url, payload)
				.then((response) => {
					vm.loading = false;
					vm.$emit("is-transacting", false);
					vm.$notifications.add({
						message: successMessage,
						type: "success",
						title: this.$t("account.deposit-initiated"),
						duration: 15000,
					});
					this.analyticsTrack("deposit", "success", {
						mobile: vm.user.mobile,
						user_id: vm.user.id,
						wallet_balance: vm.user.balance,
						depositAmount: vm.$options.filters.currency(vm.amount),
						depositMethod: "ONLINE",
						message: "Success",
						isFirstDeposit: 0,
					});
					vm.amount = null;
					vm.$emit("close");
					if (response.data.url) window.open(response.data.url);
				})
				.catch((error) => {
					vm.loading = false;
					vm.$emit("is-transacting", false);
					this.analyticsTrack("deposit", "failed", {
						mobile: vm.user.mobile,
						user_id: vm.user.id,
						wallet_balance: vm.user.balance,
						depositAmount: vm.$options.filters.currency(vm.amount),
						depositMethod: "ONLINE",
						message: error.response
							? error.response.data.error.message + errorMessage
							: error.message + errorMessage,
					});
					if (error.response) {
						if (error.response.status == 401) {
							vm.$auth.logout();
							vm.$router.replace("/login");
							return;
						}
						try {
							vm.$notifications.add({
								message: error.response.data.error.message + errorMessage,
								type: "error",
								title: this.$t("account.deposit-failed"),
								duration: 5000,
							});
						} catch (e) {
							vm.$notifications.add({
								message:
									this.$t("session.an-error-occured-please-try-again") +
									errorMessage,
								type: "error",
								title: this.$t("account.deposit-failed"),
								duration: 5000,
							});
						}
					} else {
						vm.$notifications.add({
							message:
								this.$t("session.an-error-occured-please-try-again") +
								errorMessage,
							type: "error",
							title: this.$t("account.deposit-failed"),
							duration: 5000,
						});
					}
				});
		},
		async performMiniappDeposit(mobile, depositAmount, payBillNo, currency) {
			let vm = this;

			let successMessage = vm.$t(
				"account.success-please-check-your-phone-to-complete-the-deposit"
			);

			let errorMessage = vm.$t("session.an-error-occured-please-try-again");

			const response = await vm.$miniappDeposit.miniAppDeposit(
				mobile,
				depositAmount,
				payBillNo,
				currency
			);
			if (response) {
				vm.loading = false;
				vm.$emit("is-transacting", false);
				vm.$notifications.add({
					message: successMessage,
					type: "success",
					title: this.$t("account.deposit-initiated"),
					duration: 15000,
				});
				this.analyticsTrack("deposit", "success", {
					mobile: vm.user.mobile,
					user_id: vm.user.id,
					wallet_balance: vm.user.balance,
					depositAmount: vm.$options.filters.currency(vm.amount),
					depositMethod: "ONLINE",
					message: "Success",
					isFirstDeposit: 0,
				});
				vm.amount = null;
				vm.$emit("close");

				return true;
			}

			vm.loading = false;
			vm.$emit("is-transacting", false);
			this.analyticsTrack("deposit", "failed", {
				mobile: vm.user.mobile,
				user_id: vm.user.id,
				wallet_balance: vm.user.balance,
				depositAmount: vm.$options.filters.currency(vm.amount),
				depositMethod: "ONLINE",
				message: "Miniapp deposit failed",
			});

			vm.$notifications.add({
				message: errorMessage,
				type: "error",
				title: this.$t("account.deposit-failed"),
				duration: 5000,
			});

			return false;
		},
		close() {
			this.closeBottomSheet();
		},
		handleValidationError(message) {
			this.error = message;
		},
	},
};
</script>
<style lang="scss" scoped>
.deposit {
	margin-bottom: 15px;
	&__desc {
		&--error {
			color: var(--red);
			background: var(--light-red);
			padding: 5px;
			border-radius: 5px;
		}
	}

	.input.account__section__input > input {
		background: var(--white);
		border-radius: 4px;
	}
	.account__section__submit {
		&.cashia {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 10px;
			color: white !important;
			font-weight: var(--font-weight-bold);
			background-color: var(--bg-green-600) !important;
			.img-cover{
				background-color: var(--always-white);
				border-radius: 50%;
				line-height: 24px;
				height: 24px;
				width: 24px;
				overflow: hidden;
				position: relative;
				img{
					width: 100%;
				}
			}
		}
	}
}
</style>
